import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, KeyRound, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PasswordInput } from "@/components/PasswordInput";
import { toast } from "sonner";

export default function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { setSession } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("Password must be at least 6 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/set-password", { token, password });
      setSession(data);
      toast.success("Password set — you're logged in!");
      nav("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not set password.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[80vh] grid place-items-center px-4 py-16" data-testid="set-password-page">
      <div className="w-full max-w-md eyw-card p-8">
        <span className="grid place-items-center h-12 w-12 rounded-full bg-[#144F75]/10 text-[#144F75] mb-4"><KeyRound size={22} /></span>
        <h1 className="font-serif text-3xl text-[#1A242B]">Set your password</h1>
        <p className="text-[#4A5D6B] mt-2 text-sm">Choose a password to access your Enhancing Your World account.</p>
        {!token ? (
          <p className="text-red-600 mt-6 text-sm" data-testid="set-password-notoken">This link is missing its token. Please use the link from your email or <Link to="/forgot-password" className="text-[#144F75] underline">request a new one</Link>.</p>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password" testid="set-password-input" />
            <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm password" testid="set-password-confirm" />
            {error && <p className="text-sm text-red-600" data-testid="set-password-error">{error}</p>}
            <button type="submit" disabled={loading} data-testid="set-password-submit" className="eyw-btn w-full disabled:opacity-60">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <CheckCircle2 size={18} />} Set password & log in
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
